type SpanKind = 'internal' | 'client'

type AttributeValue = string | number | boolean

interface SpanRecord {
  traceId: string
  spanId: string
  parentSpanId?: string
  name: string
  kind: SpanKind
  startTimeUnixNano: string
  endTimeUnixNano: string
  attributes: Record<string, AttributeValue>
  error?: boolean
}

interface SpanHandle {
  traceId: string
  spanId: string
  traceparent: string
  end: (options?: { status?: number; error?: unknown }) => void
}

const SERVICE_NAME = import.meta.env.VITE_OTEL_SERVICE_NAME || 'argus-frontend'
const OTLP_ENDPOINT = normalizeEndpoint(import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT)
const ENABLED = readEnabled()
const LONG_TASK_THRESHOLD_MS = 250

let queue: SpanRecord[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let currentRouteContext: Pick<SpanRecord, 'traceId' | 'spanId'> | null = null
let initialized = false

export function initFrontendTelemetry(): void {
  if (initialized || !isBrowser()) return
  initialized = true

  recordNavigation('initial')
  recordPageLoad()

  window.addEventListener('hashchange', () => recordNavigation('hashchange'))
  window.addEventListener('popstate', () => recordNavigation('popstate'))
  window.addEventListener('error', (event) => {
    recordClientError('client.error', event.error ?? event.message)
  })
  window.addEventListener('unhandledrejection', (event) => {
    recordClientError('client.unhandled_rejection', event.reason)
  })
  observeLongTasks()
}

export function startApiRequestSpan(method: string, path: string): SpanHandle {
  const route = sanitizePath(path)
  return startSpan(`HTTP ${method} ${route}`, 'client', {
    'argus.workflow': workflowForPath(route),
    'http.request.method': method,
    'http.route': route,
  })
}

export function recordClientError(name: string, error: unknown): void {
  const span = startSpan(name, 'internal', {
    'argus.workflow': 'frontend_error',
    'error.type': errorType(error),
    'url.path': sanitizePath(window.location.hash || window.location.pathname),
  })
  span.end({ error })
}

function recordNavigation(source: string): void {
  const route = sanitizePath(window.location.hash || window.location.pathname)
  const span = startSpan('navigation', 'internal', {
    'argus.workflow': 'navigation',
    'navigation.source': source,
    'url.path': route,
  })
  currentRouteContext = { traceId: span.traceId, spanId: span.spanId }
  span.end()
}

function recordPageLoad(): void {
  window.addEventListener(
    'load',
    () => {
      const nav = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined
      const attributes: Record<string, AttributeValue> = {
        'argus.workflow': 'page_load',
        'url.path': sanitizePath(window.location.hash || window.location.pathname),
      }
      if (nav) {
        attributes['page.dom_content_loaded_ms'] = Math.round(nav.domContentLoadedEventEnd)
        attributes['page.load_event_ms'] = Math.round(nav.loadEventEnd)
        attributes['page.response_end_ms'] = Math.round(nav.responseEnd)
      }
      const span = startSpan('page.load', 'internal', attributes)
      span.end()
    },
    { once: true },
  )
}

function observeLongTasks(): void {
  if (!('PerformanceObserver' in window)) return
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration < LONG_TASK_THRESHOLD_MS) continue
        const span = startSpan('performance.long_task', 'internal', {
          'argus.workflow': 'frontend_performance',
          'performance.entry_type': entry.entryType,
          'performance.duration_ms': Math.round(entry.duration),
          'url.path': sanitizePath(window.location.hash || window.location.pathname),
        })
        span.end()
      }
    })
    observer.observe({ entryTypes: ['longtask'] })
  } catch {
    // Long Task API support varies by browser; telemetry should never affect UX.
  }
}

function startSpan(
  name: string,
  kind: SpanKind,
  attributes: Record<string, AttributeValue>,
): SpanHandle {
  const traceId = currentRouteContext?.traceId ?? randomHex(16)
  const spanId = randomHex(8)
  const parentSpanId = currentRouteContext?.spanId
  const started = nowUnixNano()
  let ended = false

  return {
    traceId,
    spanId,
    traceparent: `00-${traceId}-${spanId}-01`,
    end: (options = {}) => {
      if (ended) return
      ended = true
      enqueue({
        traceId,
        spanId,
        parentSpanId,
        name,
        kind,
        startTimeUnixNano: started,
        endTimeUnixNano: nowUnixNano(),
        attributes: {
          ...attributes,
          ...(options.status
            ? {
                'http.response.status_code': options.status,
                'http.response.status_class': `${Math.floor(options.status / 100)}xx`,
              }
            : {}),
          ...(options.error
            ? {
                'error.type': errorType(options.error),
              }
            : {}),
        },
        error: Boolean(options.error || (options.status && options.status >= 500)),
      })
    },
  }
}

function enqueue(span: SpanRecord): void {
  if (!ENABLED || !OTLP_ENDPOINT) return
  queue.push(span)
  if (flushTimer !== null) return
  flushTimer = setTimeout(flush, 500)
}

function flush(): void {
  flushTimer = null
  const spans = queue
  queue = []
  if (!spans.length || !OTLP_ENDPOINT) return

  const body = JSON.stringify(toOtlpPayload(spans))
  const blob = new Blob([body], { type: 'application/json' })
  if (navigator.sendBeacon?.(OTLP_ENDPOINT, blob)) {
    return
  }
  void fetch(OTLP_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Telemetry export failures must never break the app.
  })
}

function toOtlpPayload(spans: SpanRecord[]): unknown {
  return {
    resourceSpans: [
      {
        resource: {
          attributes: [
            otlpAttribute('service.name', SERVICE_NAME),
            otlpAttribute('service.namespace', 'argus'),
            otlpAttribute('deployment.environment', import.meta.env.MODE),
          ],
        },
        scopeSpans: [
          {
            scope: { name: 'argus.frontend' },
            spans: spans.map((span) => ({
              traceId: span.traceId,
              spanId: span.spanId,
              parentSpanId: span.parentSpanId,
              name: span.name,
              kind: span.kind === 'client' ? 3 : 1,
              startTimeUnixNano: span.startTimeUnixNano,
              endTimeUnixNano: span.endTimeUnixNano,
              attributes: Object.entries(safeAttributes(span.attributes)).map(([key, value]) =>
                otlpAttribute(key, value),
              ),
              status: span.error ? { code: 2 } : { code: 1 },
            })),
          },
        ],
      },
    ],
  }
}

function otlpAttribute(key: string, value: AttributeValue): unknown {
  if (typeof value === 'number') return { key, value: { doubleValue: value } }
  if (typeof value === 'boolean') return { key, value: { boolValue: value } }
  return { key, value: { stringValue: value } }
}

function safeAttributes(
  attributes: Record<string, AttributeValue>,
): Record<string, AttributeValue> {
  const out: Record<string, AttributeValue> = {}
  for (const [key, value] of Object.entries(attributes)) {
    if (/authorization|password|secret|token|email|username/i.test(key)) continue
    out[key] = typeof value === 'string' ? value.slice(0, 256) : value
  }
  return out
}

function sanitizePath(path: string): string {
  const withoutQuery = path.split('?')[0]?.split('#').pop() || '/'
  return withoutQuery
    .replace(/\/[0-9]+(?=\/|$)/g, '/:id')
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id')
}

function workflowForPath(path: string): string {
  if (path.includes('/attachments/') && path.endsWith('/download')) return 'document_download'
  if (path.endsWith('/ai-analysis')) return 'ai_analysis'
  if (path.includes('/notifications')) return 'notification'
  if (path.includes('/export') || path.endsWith('/exports')) return 'export_generation'
  return 'api_request'
}

function errorType(error: unknown): string {
  if (error instanceof Error) return error.name || 'Error'
  if (typeof error === 'string') return 'Error'
  return 'UnknownError'
}

function randomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function nowUnixNano(): string {
  const ms =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.timeOrigin + performance.now()
      : Date.now()
  return Math.round(ms * 1_000_000).toString()
}

function normalizeEndpoint(value: string | undefined): string {
  if (!value) return ''
  const trimmed = value.replace(/\/+$/, '')
  return trimmed.endsWith('/v1/traces') ? trimmed : `${trimmed}/v1/traces`
}

function readEnabled(): boolean {
  if (!OTLP_ENDPOINT) return false
  const raw = import.meta.env.VITE_OTEL_ENABLED
  if (raw === undefined) return true
  return !['0', 'false', 'no', 'off'].includes(raw.trim().toLowerCase())
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}
